import { Col, Modal, Row, Typography, Table, Button } from 'antd';

import { useEffect, useState } from 'react';

import { ColumnsType } from 'antd/lib/table';

import { useIntl } from 'react-intl';

import { Key } from 'antd/es/table/interface';

import { applyMask } from '@/utils/mask';

import { cnpjMask } from '@/utils/mask/commonMasks';

import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

import { ModalProps } from '@/types/ModalProps';

import LoadMoreSummary from '../LoadMoreSummary';
import { useInsurers } from '@/core/domain/people/Insurers/Insurers.hooks';
import {
  Insurer,
  ListInsurersQuery,
} from '@/core/domain/people/Insurers/Insurers.types';
import InsurersSelectedFilters from './InsurersSelectedFilters';

import { CheckOutlined } from '@ant-design/icons';
import { useDebounce } from '@/hooks';

interface SelectedInsurersProps extends ModalProps {
  finishCallback?: (insurers?: Insurer[]) => void;
  selectedInsurers?: Insurer[];
  selectedInsurerIds?: number[];
  noSelection?: boolean;
  onInsurersSelect?: (insurer: Insurer) => void;
}

const InsurersSelectedModal: React.FC<SelectedInsurersProps> = ({
  visible,
  finishCallback,
  handleVisible,
  selectedInsurers = [],
  selectedInsurerIds,
  noSelection,
  onInsurersSelect,
}) => {
  const intl = useIntl();

  const { Title } = Typography;

  const [queryParams, setQueryParams] = useState<ListInsurersQuery>({
    order: 'ASC',
    active: true,
  });

  const [selectedInsurersKeys, setSelectedInsurersKeys] = useState<Key[]>([]);

  const queryDebounce = useDebounce(queryParams);
  const { data, isFetching, hasNextPage, fetchNextPage } = useInsurers(
    queryDebounce,
    visible,
  );

  const insurers = flatInfiniteData(data);

  useEffect(() => {
    if (visible) {
      if (selectedInsurerIds) {
        return setSelectedInsurersKeys(selectedInsurerIds);
      }
      setSelectedInsurersKeys(selectedInsurers.map((insurer) => insurer.id));
    }
  }, [visible]);

  const handleOk = (): void => {
    const employee = getSelectedInsurers();

    if (finishCallback) {
      finishCallback(employee);
    }

    handleCancel();
  };

  const handleCancel = (): void => {
    setQueryParams({ order: 'ASC', active: true });

    setSelectedInsurersKeys([]);

    handleVisible();
  };

  const columns: ColumnsType<Insurer> = [
    {
      dataIndex: 'name',
      title: intl.formatMessage({ id: 'insurers.list.header.name' }),
      ellipsis: true,
      width: '40%',
    },
    {
      dataIndex: 'fantasyName',
      title: intl.formatMessage({ id: 'insurers.list.header.fantasyName' }),
      ellipsis: true,
      width: '40%',
    },
    {
      dataIndex: 'cnpj',
      title: intl.formatMessage({ id: 'insurers.list.header.cnpj' }),
      render: (value: string) => {
        if (!value) {
          return '-';
        }
        if (value?.length === 14) {
          return applyMask(value, cnpjMask);
        }
        return value;
      },
      ellipsis: true,
      width: '40%',
    },
    onInsurersSelect
      ? {
          dataIndex: 'actions',
          align: 'right',
          render: (_, row) => (
            <Button
              type="primary"
              onClick={() => onInsurersSelect(row)}
              icon={<CheckOutlined />}
            ></Button>
          ),
          width: '20%',
        }
      : {},
  ];

  const handleInsurersSelect = (selectedRowKeys: Key[]) => {
    setSelectedInsurersKeys(selectedRowKeys);
  };

  const getSelectedInsurers = (): Insurer[] => {
    const result = insurers.filter((insurer) => {
      return selectedInsurersKeys.includes(insurer.id);
    });

    return result;
  };

  return (
    <Modal
      title={intl.formatMessage({
        id: 'insurers.modal.selectedInsurers.title.add',
      })}
      visible={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      width={800}
    >
      <Row gutter={16}>
        <Col flex={1}>
          <div style={{ width: '100%' }}>
            <div style={{ marginBottom: '24px' }}>
              <Title level={4}>
                {intl.formatMessage({
                  id: 'insurers.modal.selectedInsurers.search',
                })}
              </Title>
            </div>

            <InsurersSelectedFilters setQueryParams={setQueryParams} />

            <Table<Insurer>
              columns={columns}
              dataSource={insurers}
              loading={isFetching}
              rowKey={(row) => row.id}
              pagination={false}
              summary={() =>
                hasNextPage ? (
                  <LoadMoreSummary
                    onClick={fetchNextPage}
                    columnsCount={columns.length + 1}
                  />
                ) : null
              }
              rowSelection={
                !noSelection
                  ? {
                      selectedRowKeys: selectedInsurersKeys,
                      onChange: handleInsurersSelect,
                    }
                  : undefined
              }
            />
          </div>
        </Col>
      </Row>
    </Modal>
  );
};

export default InsurersSelectedModal;
